import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import client from '../api/client'
import { useAuthStore } from '../store/authStore'
import { toast } from '../components/Toast'
import Spinner from '../components/Spinner'

const MBTI_LIST = ['INTJ','INTP','ENTJ','ENTP','INFJ','INFP','ENFJ','ENFP','ISTJ','ISFJ','ESTJ','ESFJ','ISTP','ISFP','ESTP','ESFP']

type SajuInfo = {
  year_gan: string; year_ji: string; month_gan: string; month_ji: string
  day_gan: string; day_ji: string; time_gan?: string; time_ji?: string
  element_wood: number; element_fire: number; element_earth: number
  element_metal: number; element_water: number; summary?: string
} | null

function ElementBar({ label, value, color }: { label: string; value: number; color: string }) {
  const max = 8
  const pct = Math.round((value / max) * 100)
  return (
    <div className="flex items-center gap-2">
      <span className="text-[var(--color-text-tertiary)] text-xs w-8">{label}</span>
      <div className="flex-1 bg-[var(--color-border-default)] rounded-full h-2">
        <div className={`h-2 rounded-full transition-all ${color}`} style={{ width: `${Math.min(pct, 100)}%` }} />
      </div>
      <span className="text-[var(--color-text-disabled)] text-xs w-3">{value}</span>
    </div>
  )
}

export default function Profile() {
  const navigate = useNavigate()
  const { user, setUser, isAuthenticated, logout } = useAuthStore()
  const [form, setForm] = useState({ nickname: '', birth_time: '', mbti: '', blood_type: '' })
  const [editing, setEditing] = useState<'basic' | 'personality' | null>(null)
  const [loading, setLoading] = useState(false)
  const [saju, setSaju] = useState<SajuInfo>(null)
  const [sajuLoading, setSajuLoading] = useState(false)
  const [partners, setPartners] = useState<any[]>([])
  const [partnersLoading, setPartnersLoading] = useState(false)
  const [showWithdrawModal, setShowWithdrawModal] = useState(false)
  const [showPasswordForm, setShowPasswordForm] = useState(false)
  const [pwForm, setPwForm] = useState({ current: '', next: '', confirm: '' })
  const [pwLoading, setPwLoading] = useState(false)
  const [showBirthModal, setShowBirthModal] = useState(false)
  const [birthForm, setBirthForm] = useState({ birth_date: '', birth_type: 'solar' })
  const [birthLoading, setBirthLoading] = useState(false)
  const [myReview, setMyReview] = useState<{ rating: number; content: string } | null>(null)
  const [reviewLoading, setReviewLoading] = useState(false)

  useEffect(() => {
    if (!isAuthenticated) { navigate('/login'); return }
    if (user) {
      setForm({ nickname: user.nickname, birth_time: user.birth_time ?? '', mbti: user.mbti ?? '', blood_type: user.blood_type ?? '' })
    }
    loadSaju()
    loadMyReview()
    loadPartners()
  }, [user, isAuthenticated])

  const loadPartners = async () => {
    setPartnersLoading(true)
    try {
      const { data } = await client.get('/partners')
      setPartners(data)
    } catch {
      toast('파트너 목록을 불러오지 못했습니다.', 'error')
    } finally {
      setPartnersLoading(false)
    }
  }

  const loadSaju = async () => {
    setSajuLoading(true)
    try {
      const { data } = await client.get('/users/me/saju')
      setSaju(data)
    } catch { setSaju(null) }
    finally { setSajuLoading(false) }
  }

  const loadMyReview = async () => {
    if (!user) return
    setReviewLoading(true)
    try {
      const { data } = await client.get<{ id: number; user_id: number; rating: number; content: string; created_at: string }[]>('/reviews')
      const mine = data.find((r) => r.user_id === user.id)
      setMyReview(mine ? { rating: mine.rating, content: mine.content } : null)
    } catch {
      setMyReview(null)
    } finally {
      setReviewLoading(false)
    }
  }

  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }))

  const handleSave = async () => {
    setLoading(true)
    try {
      const { data } = await client.put('/users/me', {
        nickname: form.nickname,
        birth_time: form.birth_time || null,
        mbti: form.mbti || null,
        blood_type: form.blood_type || null,
      })
      setUser(data)
      setEditing(null)
      toast('저장되었습니다.', 'success')
    } catch {
      toast('저장에 실패했습니다.', 'error')
    } finally {
      setLoading(false)
    }
  }

  const handlePasswordChange = async () => {
    if (pwForm.next !== pwForm.confirm) {
      toast('새 비밀번호가 일치하지 않습니다.', 'error'); return
    }
    if (pwForm.next.length < 6) {
      toast('비밀번호는 6자 이상이어야 합니다.', 'error'); return
    }
    setPwLoading(true)
    try {
      await client.put('/users/me/password', { current_password: pwForm.current, new_password: pwForm.next })
      setPwForm({ current: '', next: '', confirm: '' })
      setShowPasswordForm(false)
      toast('비밀번호가 변경되었습니다.', 'success')
    } catch (e: any) {
      toast(e?.response?.data?.detail || '비밀번호 변경에 실패했습니다.', 'error')
    } finally {
      setPwLoading(false)
    }
  }

  const handleBirthChange = async () => {
    if (!birthForm.birth_date) { toast('변경할 생년월일을 입력하세요.', 'error'); return }
    setBirthLoading(true)
    try {
      const { data } = await client.put('/users/me', {
        birth_date: birthForm.birth_date,
        birth_type: birthForm.birth_type,
      })
      setUser(data)
      await loadSaju()
      setShowBirthModal(false)
      toast('생년월일이 변경되고 사주가 재계산되었습니다.', 'success')
    } catch {
      toast('변경에 실패했습니다.', 'error')
    } finally {
      setBirthLoading(false)
    }
  }

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  const handleWithdraw = async () => {
    try {
      await client.delete('/users/me')
      logout()
      navigate('/')
      toast('탈퇴가 완료되었습니다.', 'success')
    } catch {
      toast('탈퇴에 실패했습니다.', 'error')
    }
  }

  const handleDeletePartner = async (id: number) => {
    try {
      await client.delete(`/partners/${id}`)
      setPartners(partners.filter(p => p.id !== id))
      toast('삭제되었습니다.', 'success')
    } catch {
      toast('삭제에 실패했습니다.', 'error')
    }
  }

  const inputCls = "w-full bg-[var(--color-bg)] border border-[var(--color-border-default)] rounded-lg px-4 py-2.5 text-[var(--color-text-primary)] focus:outline-none focus:border-[var(--color-border-accent)] transition-colors text-sm"
  const toggleBtn = (active: boolean) =>
    `flex-1 py-2 rounded-lg border text-sm font-medium transition-colors ${active ? 'bg-[var(--color-accent)] border-[var(--color-accent)] text-[var(--color-primary)] font-bold' : 'bg-[var(--color-bg)] border-[var(--color-border-default)] text-[var(--color-text-tertiary)] hover:border-[var(--color-border-accent)]'}`

  if (!user) return null

  return (
    <div className="min-h-screen bg-[var(--color-bg)] p-4 sm:p-6">
      <div className="max-w-lg mx-auto space-y-4">
        {/* 헤더 */}
        <div className="flex items-center gap-3 mb-2">
          <button onClick={() => navigate('/dashboard')} className="text-[var(--color-text-tertiary)] hover:text-[var(--color-text-primary)] transition-colors p-1">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div>
            <h1 className="text-xl font-bold text-[var(--color-text-primary)]">내 정보</h1>
            <p className="text-[var(--color-text-tertiary)] text-xs">단골 손님 정보 관리</p>
          </div>
        </div>

        {/* 캐릭터 카드 썸네일 */}
        <div className="bg-[var(--color-surface)] border border-[var(--color-border-default)] rounded-2xl p-5 flex items-center gap-4">
          <div className="w-16 h-16 rounded-xl bg-[var(--color-accent)]/10 border border-[var(--color-accent)]/20 flex items-center justify-center text-3xl">
            {saju ? (saju.element_fire > saju.element_water ? '🔥' : saju.element_water > saju.element_fire ? '💧' : saju.element_wood > saju.element_metal ? '🌿' : saju.element_metal > saju.element_wood ? '⚙️' : '🏔️') : '🎴'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[var(--color-text-primary)] font-semibold">{user.nickname}</p>
            <p className="text-[var(--color-text-tertiary)] text-sm">
              {saju?.day_gan}토 일간
              {user.mbti && ` · ${user.mbti}`}
              {user.blood_type && ` · ${user.blood_type}형`}
            </p>
          </div>
          <button onClick={() => navigate('/features/character-card')}
            className="text-[var(--color-accent)] hover:opacity-80 text-sm transition-opacity flex-shrink-0">
            <span className="hidden sm:inline">카드 보러가기</span>
            <span className="sm:hidden">카드</span>
            {' →'}
          </button>
        </div>

        {/* 기본 정보 */}
        <div className="bg-[var(--color-surface)] border border-[var(--color-border-default)] rounded-2xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-[var(--color-text-primary)] font-semibold">📋 기본 정보</h2>
            <button onClick={() => setEditing(editing === 'basic' ? null : 'basic')}
              className="text-[var(--color-accent)] hover:opacity-80 text-sm transition-opacity">
              {editing === 'basic' ? '취소' : '수정'}
            </button>
          </div>
          <div className="space-y-3">
            {editing === 'basic' ? (
              <>
                <div>
                  <label className="block text-xs text-[var(--color-text-tertiary)] mb-1">닉네임</label>
                  <input type="text" className={inputCls} value={form.nickname} onChange={(e) => set('nickname', e.target.value)} />
                </div>
                <div>
                  <label className="block text-xs text-[var(--color-text-tertiary)] mb-1">태어난 시간 (선택)</label>
                  <input type="time" className={inputCls} value={form.birth_time} onChange={(e) => set('birth_time', e.target.value)} />
                </div>
                <button onClick={handleSave} disabled={loading}
                  className="w-full py-2 bg-[var(--color-accent)] hover:opacity-90 disabled:opacity-50 text-[var(--color-primary)] rounded-xl font-bold text-sm transition-opacity">
                  {loading ? '저장 중...' : '저장'}
                </button>
              </>
            ) : (
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-[var(--color-text-tertiary)]">닉네임</span><span className="text-[var(--color-text-primary)]">{user.nickname}</span></div>
                <div className="flex justify-between"><span className="text-[var(--color-text-tertiary)]">이메일</span><span className="text-[var(--color-text-secondary)]">{user.email}</span></div>
                <div className="flex justify-between"><span className="text-[var(--color-text-tertiary)]">성별</span><span className="text-[var(--color-text-secondary)]">{user.gender === 'M' ? '남성' : '여성'}</span></div>
              </div>
            )}
          </div>
        </div>

        {/* 성격 정보 */}
        <div className="bg-[var(--color-surface)] border border-[var(--color-border-default)] rounded-2xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-[var(--color-text-primary)] font-semibold">🔤 성격 정보</h2>
            <button onClick={() => setEditing(editing === 'personality' ? null : 'personality')}
              className="text-[var(--color-accent)] hover:opacity-80 text-sm transition-opacity">
              {editing === 'personality' ? '취소' : '수정'}
            </button>
          </div>
          {editing === 'personality' ? (
            <div className="space-y-3">
              <div>
                <label className="block text-xs text-[var(--color-text-tertiary)] mb-1">MBTI</label>
                <select className={inputCls} value={form.mbti} onChange={(e) => set('mbti', e.target.value)}>
                  <option value="">선택 안함</option>
                  {MBTI_LIST.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-[var(--color-text-tertiary)] mb-1">혈액형</label>
                <div className="flex flex-wrap gap-2">
                  {['', 'A', 'B', 'O', 'AB'].map((v) => (
                    <button key={v} type="button" onClick={() => set('blood_type', v)} className={toggleBtn(form.blood_type === v)}>
                      {v || '?'}
                    </button>
                  ))}
                </div>
              </div>
              <button onClick={handleSave} disabled={loading}
                className="w-full py-2 bg-[var(--color-accent)] hover:opacity-90 disabled:opacity-50 text-[var(--color-primary)] rounded-xl font-bold text-sm transition-opacity">
                {loading ? '저장 중...' : '저장'}
              </button>
            </div>
          ) : (
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-[var(--color-text-tertiary)]">MBTI</span><span className="text-[var(--color-text-secondary)]">{user.mbti || '미입력'}</span></div>
              <div className="flex justify-between"><span className="text-[var(--color-text-tertiary)]">혈액형</span><span className="text-[var(--color-text-secondary)]">{user.blood_type ? `${user.blood_type}형` : '미입력'}</span></div>
            </div>
          )}
        </div>

        {/* 사주 정보 */}
        <div className="bg-[var(--color-surface)] border border-[var(--color-border-default)] rounded-2xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-[var(--color-text-primary)] font-semibold">🌟 사주 정보</h2>
            <button onClick={() => { setBirthForm({ birth_date: user.birth_date, birth_type: user.birth_type }); setShowBirthModal(true) }}
              className="text-[var(--color-accent)] hover:opacity-80 text-sm transition-opacity">
              생년월일 변경
            </button>
          </div>
          <div className="space-y-2 text-sm mb-4">
            <div className="flex justify-between"><span className="text-[var(--color-text-tertiary)]">생년월일</span><span className="text-[var(--color-text-secondary)]">{user.birth_date} ({user.birth_type === 'solar' ? '양력' : '음력'})</span></div>
            {user.birth_time && <div className="flex justify-between"><span className="text-[var(--color-text-tertiary)]">태어난 시간</span><span className="text-[var(--color-text-secondary)]">{user.birth_time}</span></div>}
          </div>

          {sajuLoading ? (
            <div className="flex justify-center py-4"><Spinner /></div>
          ) : saju ? (
            <>
              <div className="bg-[var(--color-accent)]/10 border border-[var(--color-accent)]/20 rounded-xl px-4 py-3 mb-4">
                <p className="text-xs text-[var(--color-text-tertiary)] mb-2">사주팔자</p>
                <p className="text-[var(--color-accent)] font-medium break-words">
                  {saju.year_gan}{saju.year_ji}년 {saju.month_gan}{saju.month_ji}월 {saju.day_gan}{saju.day_ji}일
                  {saju.time_gan && ` ${saju.time_gan}${saju.time_ji}시`}
                </p>
              </div>
              <div>
                <p className="text-xs text-[var(--color-text-tertiary)] mb-2">오행 분포</p>
                <div className="space-y-2">
                  <ElementBar label="목" value={saju.element_wood} color="bg-green-500" />
                  <ElementBar label="화" value={saju.element_fire} color="bg-red-500" />
                  <ElementBar label="토" value={saju.element_earth} color="bg-yellow-600" />
                  <ElementBar label="금" value={saju.element_metal} color="bg-gray-400" />
                  <ElementBar label="수" value={saju.element_water} color="bg-blue-500" />
                </div>
              </div>
            </>
          ) : (
            <p className="text-[var(--color-text-disabled)] text-sm">사주 정보를 불러올 수 없습니다.</p>
          )}
        </div>

        {/* 저장된 상대방 */}
        <div className="bg-[var(--color-surface)] border border-[var(--color-border-default)] rounded-2xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-[var(--color-text-primary)] font-semibold">👥 저장된 상대방</h2>
          </div>
          {partnersLoading ? (
            <div className="flex justify-center py-4"><Spinner /></div>
          ) : partners.length > 0 ? (
            <div className="space-y-2">
              {partners.map(p => (
                <div key={p.id} className="flex items-center justify-between p-3 bg-[var(--color-bg)] border border-[var(--color-border-default)] rounded-xl">
                  <span className="text-sm text-[var(--color-text-primary)] font-medium">{p.nickname}</span>
                  <button onClick={() => handleDeletePartner(p.id)}
                    className="text-[var(--color-error)] hover:opacity-80 transition-opacity p-1">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center py-4 text-[var(--color-text-disabled)] text-sm">저장된 상대방이 없습니다.</p>
          )}
        </div>

        {/* 내 후기 */}
        <div className="bg-[var(--color-surface)] border border-[var(--color-border-default)] rounded-2xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-[var(--color-text-primary)] font-semibold">⭐ 내 후기</h2>
            <button onClick={() => navigate('/review')}
              className="text-[var(--color-accent)] hover:opacity-80 text-sm transition-opacity">
              {myReview ? '수정하기' : '작성하기'}
            </button>
          </div>
          {reviewLoading ? (
            <div className="flex justify-center py-2"><Spinner /></div>
          ) : myReview ? (
            <div className="space-y-2">
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((s) => (
                  <span key={s} className={`text-xl ${s <= myReview.rating ? 'text-[var(--color-accent)]' : 'text-[var(--color-border-default)]'}`}>★</span>
                ))}
              </div>
              <p className="text-sm text-[var(--color-text-secondary)] leading-relaxed">{myReview.content}</p>
            </div>
          ) : (
            <div className="text-center py-2">
              <p className="text-[var(--color-text-disabled)] text-sm mb-3">아직 후기를 남기지 않으셨어요.</p>
              <button onClick={() => navigate('/review')}
                className="px-5 py-2 bg-[var(--color-accent)] hover:opacity-90 text-[var(--color-primary)] rounded-xl text-sm font-bold transition-opacity">
                후기 남기기
              </button>
            </div>
          )}
        </div>

        {/* 문의 / 건의 */}
        <div className="bg-[var(--color-surface)] border border-[var(--color-border-default)] rounded-2xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-[var(--color-text-primary)] font-semibold">📮 문의 / 건의</h2>
          </div>
          <div className="space-y-2">
            <button onClick={() => navigate('/inquiry/new')}
              className="w-full text-left px-4 py-3 rounded-xl text-sm text-[var(--color-text-tertiary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg)] transition-colors">
              문의 / 건의 작성
            </button>
            <button onClick={() => navigate('/inquiries')}
              className="w-full text-left px-4 py-3 rounded-xl text-sm text-[var(--color-text-tertiary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg)] transition-colors">
              내 문의 내역
            </button>
          </div>
        </div>

        {/* 계정 관리 */}
        <div className="bg-[var(--color-surface)] border border-[var(--color-border-default)] rounded-2xl p-5">
          <h2 className="text-[var(--color-text-primary)] font-semibold mb-4">🔒 계정 관리</h2>
          <div className="space-y-2">
            <button onClick={() => setShowPasswordForm(!showPasswordForm)}
              className="w-full text-left px-4 py-3 rounded-xl text-sm text-[var(--color-text-tertiary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg)] transition-colors">
              비밀번호 변경
            </button>
            {showPasswordForm && (
              <div className="px-1 pb-2 space-y-2">
                <input type="password" placeholder="현재 비밀번호" className={inputCls}
                  value={pwForm.current} onChange={(e) => setPwForm((f) => ({ ...f, current: e.target.value }))} />
                <input type="password" placeholder="새 비밀번호 (6자 이상)" className={inputCls}
                  value={pwForm.next} onChange={(e) => setPwForm((f) => ({ ...f, next: e.target.value }))} />
                <input type="password" placeholder="새 비밀번호 확인" className={inputCls}
                  value={pwForm.confirm} onChange={(e) => setPwForm((f) => ({ ...f, confirm: e.target.value }))} />
                <button onClick={handlePasswordChange} disabled={pwLoading}
                  className="w-full py-2 bg-[var(--color-accent)] hover:opacity-90 disabled:opacity-50 text-[var(--color-primary)] rounded-xl font-bold text-sm transition-opacity">
                  {pwLoading ? '변경 중...' : '변경하기'}
                </button>
              </div>
            )}
            <button onClick={handleLogout}
              className="w-full text-left px-4 py-3 rounded-xl text-sm text-[var(--color-text-tertiary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg)] transition-colors">
              로그아웃
            </button>
            <button onClick={() => setShowWithdrawModal(true)}
              className="w-full text-left px-4 py-3 rounded-xl text-sm text-[var(--color-error)] hover:opacity-80 hover:bg-[var(--color-error)]/10 transition-colors">
              회원 탈퇴
            </button>
          </div>
        </div>
      </div>

      {/* 생년월일 변경 모달 */}
      {showBirthModal && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-[var(--color-surface)] border border-[var(--color-border-default)] rounded-2xl p-6 w-full max-w-sm shadow-xl">
            <h2 className="text-[var(--color-text-primary)] font-bold text-lg mb-1">생년월일 변경</h2>
            <p className="text-[var(--color-text-tertiary)] text-xs mb-4">변경 시 사주팔자가 재계산되며, 기존 사주 요약·캐릭터 카드·직업 추천이 초기화됩니다.</p>
            <div className="space-y-3">
              <div>
                <label className="block text-xs text-[var(--color-text-tertiary)] mb-1">생년월일</label>
                <input type="date" className={inputCls}
                  value={birthForm.birth_date}
                  onChange={(e) => setBirthForm((f) => ({ ...f, birth_date: e.target.value }))} />
              </div>
              <div>
                <label className="block text-xs text-[var(--color-text-tertiary)] mb-1">양력 / 음력</label>
                <div className="flex gap-2">
                  {[['solar', '양력'], ['lunar', '음력']].map(([v, label]) => (
                    <button key={v} type="button"
                      onClick={() => setBirthForm((f) => ({ ...f, birth_type: v }))}
                      className={toggleBtn(birthForm.birth_type === v)}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="bg-[var(--color-accent)]/10 border border-[var(--color-accent)]/20 rounded-xl px-3 py-2 mt-3">
              <p className="text-[var(--color-accent)] text-xs">⚠️ 이 작업은 되돌릴 수 없습니다. 신중하게 변경해 주세요.</p>
            </div>
            <div className="flex gap-3 mt-4">
              <button onClick={() => setShowBirthModal(false)}
                className="flex-1 py-2.5 border border-[var(--color-border-default)] text-[var(--color-text-tertiary)] rounded-xl text-sm hover:text-[var(--color-text-primary)] transition-colors">
                취소
              </button>
              <button onClick={handleBirthChange} disabled={birthLoading}
                className="flex-1 py-2.5 bg-[var(--color-accent)] hover:opacity-90 disabled:opacity-50 text-[var(--color-primary)] rounded-xl text-sm font-bold transition-opacity">
                {birthLoading ? '재계산 중...' : '변경 확인'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 회원 탈퇴 모달 */}
      {showWithdrawModal && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-[var(--color-surface)] border border-[var(--color-border-default)] rounded-2xl p-6 w-full max-w-sm shadow-xl">
            <p className="text-2xl text-center mb-3">😢</p>
            <h2 className="text-[var(--color-text-primary)] font-bold text-lg text-center mb-2">정말 떠나실 건가요?</h2>
            <p className="text-[var(--color-text-tertiary)] text-sm text-center mb-5">
              탈퇴하면 모든 상담 내역과 정보가 삭제돼요.<br />복구가 불가능해요.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setShowWithdrawModal(false)}
                className="flex-1 py-2.5 border border-[var(--color-border-default)] text-[var(--color-text-secondary)] rounded-xl text-sm hover:text-[var(--color-text-primary)] transition-colors">
                아니요, 있을게요
              </button>
              <button onClick={handleWithdraw}
                className="flex-1 py-2.5 bg-[var(--color-error)] hover:opacity-90 text-white rounded-xl text-sm font-bold transition-opacity">
                탈퇴할게요
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
